import { Assets, Sprite, Texture } from 'pixi.js';
import { MANIFEST } from './manifest';
import type { ProceduralDef } from './types';

/** Static image files under public/ — keyed like every other asset. */
const FILE_ASSETS: Record<string, string> = {
  'ui/title-bg': 'img/title-bg.png',
  'ui/title-avatar': 'img/avatar-atlas.png',
};
const fileCache = new Map<string, Texture>();

/** Sync lookup — null until preloadFileTexture() has finished. */
export function getFileTexture(key: string): Texture | null {
  return fileCache.get(key) ?? null;
}

/** Async fetch+cache for file-based assets (title art etc.). */
export async function preloadFileTexture(key: string): Promise<Texture | null> {
  const hit = fileCache.get(key);
  if (hit) return hit;
  const url = FILE_ASSETS[key];
  if (!url) return null;
  try {
    const tex = await Assets.load<Texture>(url);
    fileCache.set(key, tex);
    return tex;
  } catch {
    return null; // missing file — screens keep their flat background
  }
}

/**
 * Asset key → Texture resolver.
 * Order: (eventually) use the key from a loaded spritesheet if present →
 * otherwise draw on canvas with the procedural generator and cache it.
 * Game code only uses this module; it never knows the texture's source.
 */
const cache = new Map<string, Texture>();

/** Filled later when an atlas is loaded: key → atlas texture */
let atlasLookup: ((key: string) => Texture | null) | null = null;

export function registerAtlas(lookup: (key: string) => Texture | null): void {
  atlasLookup = lookup;
  cache.clear(); // re-resolve so the atlas takes priority
}

function renderProcedural(def: ProceduralDef, frame: number): Texture {
  const canvas = document.createElement('canvas');
  canvas.width = def.width;
  canvas.height = def.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2d context');
  def.draw(ctx, def.width, def.height, frame);
  return Texture.from(canvas);
}

/** Single-frame texture. Specify frame for animated assets. */
export function getTexture(key: string, frame = 0): Texture {
  const cacheKey = frame === 0 ? key : `${key}#${frame}`;
  const hit = cache.get(cacheKey);
  if (hit) return hit;

  const fromAtlas = atlasLookup?.(cacheKey) ?? atlasLookup?.(key) ?? null;
  if (fromAtlas) {
    cache.set(cacheKey, fromAtlas);
    return fromAtlas;
  }

  const def = MANIFEST.get(key);
  if (!def) throw new Error(`Unknown asset key: ${key}`);
  const tex = renderProcedural(def, frame);
  cache.set(cacheKey, tex);
  return tex;
}

/** All frames of an animation. */
export function getFrames(key: string): Texture[] {
  const def = MANIFEST.get(key);
  if (!def) throw new Error(`Unknown asset key: ${key}`);
  const out: Texture[] = [];
  for (let i = 0; i < def.frames; i++) out.push(getTexture(key, i));
  return out;
}

/** Ready-made Sprite with anchor set from the manifest. */
export function makeSprite(key: string, frame = 0): Sprite {
  const def = MANIFEST.get(key);
  if (!def) throw new Error(`Unknown asset key: ${key}`);
  const sprite = new Sprite(getTexture(key, frame));
  sprite.anchor.set(def.anchorX, def.anchorY);
  return sprite;
}
